/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Component, inject, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TestCasePresentation } from '../../test-tool/models/test-case-presentation';
import { TestToolService } from '../../test-tool/services/test-tool.service';
import { AvailableAdditionalFileTypeStatus } from '../../test-tool/models/test-case-available-additional-file-types';
import { AvailableInterchangeOutStatus } from '../../test-tool/models/test-case-available-interchange-out';
import { Selectbox } from '../../controls/selectbox';


export interface AddInterchangeToTestCaseDialogResult {
  testCase: TestCasePresentation;
  interchangeIds: number[];
  selectedOutputType: number;
}

@Component({
    templateUrl: './add-interchange-to-test-case-dialog.component.html',
    styleUrls: ['./add-interchange-to-test-case-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, Selectbox]
})
export class AddInterchangeToTestCaseDialogComponent implements OnInit {

  @Input() interchangeInIds: number[] = [];

  fb = inject(UntypedFormBuilder);
  activeModal = inject(NgbActiveModal);
  testToolService = inject(TestToolService);

  title: string;
  form = this.fb.group({
    selectedTestCase: [null, Validators.required],
    selectedOutputType: [null, Validators.required]
  });

  expectedOutputTypes: Array<{ displayName: string, value: number}> = [
    { 'displayName': 'None', 'value': 0 },
    { 'displayName': 'Interchange Out', 'value': 1 },
    { 'displayName': 'ExpectedOutput Additional File', 'value': 2 }
  ];

  interchangeOutAvailability: AvailableInterchangeOutStatus = AvailableInterchangeOutStatus.Unknown;
  expectedOutputAdditionalFileAvailability: AvailableAdditionalFileTypeStatus = AvailableAdditionalFileTypeStatus.Unknown;
  testCases: TestCasePresentation[] = [];

  ngOnInit(): void {
    const totalInterchangeInCount = this.interchangeInIds.length;
    this.title = (totalInterchangeInCount < 2) ? 'Add one interchange to test case' : `Add ${totalInterchangeInCount} interchanges to test case`;
    void this.initAsync();
  }

  async initAsync(): Promise<void> {
    await Promise.all([
      this.testCases = await this.testToolService.getTestCases(),
      this.interchangeOutAvailability = await this.testToolService.getInterchangeOutAvailability(this.interchangeInIds),
      this.expectedOutputAdditionalFileAvailability = await this.testToolService.getExpectedOutputAdditionalFileAvailability(this.interchangeInIds)
    ]);
  }

  ok(): void {
    const formValue = this.form.value;
    const selectedTestCase = this.testCases.find(tc => tc.Id === formValue.selectedTestCase) ?? null;
    const result: AddInterchangeToTestCaseDialogResult = {
      testCase: selectedTestCase,
      interchangeIds: this.interchangeInIds,
      selectedOutputType: formValue.selectedOutputType
    };
    this.activeModal.close(result);
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  isDisabledOption(expectedOutputType: number): boolean {
    if (expectedOutputType === 1) {
      return this.interchangeOutAvailability !== AvailableInterchangeOutStatus.Valid;
    } else if (expectedOutputType === 2) {
      return this.expectedOutputAdditionalFileAvailability !== AvailableAdditionalFileTypeStatus.Valid;
    } else {
      return false;
    }
  }

  getAvailableAdditionalFileTypeStatusDisplayName(expectedOutputType:number) : string {
    if (expectedOutputType === 1) {
      switch(AvailableInterchangeOutStatus[this.interchangeOutAvailability]) {
        case AvailableInterchangeOutStatus[AvailableInterchangeOutStatus.Valid]:
          return '';
        case AvailableInterchangeOutStatus[AvailableInterchangeOutStatus.MissingInstances]:
          return `One or more of the selected interchanges/documents do not have an interchange out file.`;
        case AvailableInterchangeOutStatus[AvailableInterchangeOutStatus.FileUnavailable]:
          return `One or more of the interchange out files are not available.`;
        default:
          return 'Invalid. Reason: Unknown';
      }
    } else if (expectedOutputType === 2) {
      switch(AvailableAdditionalFileTypeStatus[this.expectedOutputAdditionalFileAvailability]) {
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.Valid]:
          return '';
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.MissingInstances]:
          return `Additional file type 'ExpectedOutput' is missing.`;
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.MultipleInstances]:
          return `Additional file type 'ExpectedOutput' appears more.`;
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.TooManyDocuments]:
          return `The number of additional files of type 'ExpectedOutput' does not match the total number of documents. One possible reason for this issue is that the incoming interchange matches multiple distributions.`;
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.FileUnavailable]:
          return `One or more of the additional files of type 'ExpectedOutput' are not available.`;
        case AvailableAdditionalFileTypeStatus[AvailableAdditionalFileTypeStatus.ContainsResentDocument]:
          return `One or more of the selected documents were created by a resend. These documents cannot use this feature.`;
        default:
          return 'Invalid. Reason: Unknown';
      }
    } else {
      return '';
    }
  }
}
